<script setup lang="ts">
/**
 *
 * @name PackageModal
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE MODAL QUE PERMITE CREAR PAQUETES DE CERTIFICADOS
 *
 * responsabilidades:
 * R1: PERMITE CREAR UN NUEVO PAQUETE DE CERTIFICADOS
 * R2: PERMITE ASIGNAR UN NOMBRE AL PAQUETE
 * R3: PERMITE VISUALIZAR LOS CERTIFICADOS SELECCIONADOS
 * R4: PERMITE MANEJAR EL MODO DE PRUEBA
 * R5: PERMITE NOTIFICAR EL RESULTADO DE LA OPERACIÓN
 *
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { computed, ref } from 'vue'
import { ICertificate } from '@shared/types/certificate'
import { certificateService } from '@/services/certificateService'
import { useStore } from 'vuex'
import BaseModal from '../BaseModal.vue'
/**
 * @description Tipo que extiende ICertificate con una propiedad selected
 * @typedef {ICertificate & { selected: boolean }} TCertificate
 */
type TCertificate = ICertificate & { selected: boolean }

/**
 * @description Props del componente
 * @property {boolean} modelValue - Controla la visibilidad del modal
 * @property {string} institutionId - ID de la institución
 * @property {TCertificate[]} selectedCertificates - Lista de certificados seleccionados
 * @property {boolean} isTestMode - Indica si el componente está en modo prueba
 */
const props = defineProps<{
	modelValue: boolean
	institutionId: string
	selectedCertificates: TCertificate[]
	isTestMode: boolean
}>()

/**
 * @description Store de Vuex
 */
const store = useStore()
const hasErrors = ref(false)
const hasWarnings = ref(false)
const excludedWarnings = ref<string[]>([
	'Ya existen documentos con número de control ',
	'La fecha de expedición está cercana a los 90 días pasados de la fecha actual.',
	'Ya existen documentos con las mismas características para este CURP',
	'Ya existe otro documento empaquetado y firmado con las mismas características para este CURP',
	'Ya existe otro documento en cola con las mismas características para este CURP',
	'Ya existe otro documento empaquetado y firmado para este número de control'
])

/**
 * Solo permite empaquetar si todas las advertencias del mensaje están excluidas.
 * Ignora el detalle expandido ([ Número Control: ... ]).
 */
const isExcludedWarningMessage = (message?: string): boolean => {
	if (!message?.trim()) return true

	const warningLines = message
		.split('\n')
		.map(line => line.trim())
		.filter(line => line.length > 0)
		.filter(line => !line.startsWith('[') && !line.startsWith('Número Control:'))
	if (warningLines.length === 0) return true

	return warningLines.every(line => excludedWarnings.value.some(excluded => line.includes(excluded)))
}
const showErrorModal = ref(false)
const errorMessage = ref<string>('')
/**
 * @description Emits del componente
 * @param {boolean} update:modelValue - Actualiza el valor del modal
 */
const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>()

/**
 * @description Computed property que controla la visibilidad del modal
 * @type {ComputedRef<boolean>}
 */
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})

/**
 * @description Referencia al nombre del paquete
 * @type {Ref<string>}
 */
const packageName = ref('')

/**
 * @description Computed property que obtiene el número de certificados seleccionados
 * @type {ComputedRef<number>}
 */
const selectedCertificatesCount = computed(() => props.selectedCertificates.length)

/**
 * @description Función que crea un nuevo paquete de certificados
 * @async
 * @returns {Promise<void>}
 */
const createPackage = async () => {
	if (!packageName.value) {
		showErrorModal.value = true
		errorMessage.value = 'Debe ingresar un nombre para el paquete'
		return
	}

	try {
		const certificatesMap = props.selectedCertificates.reduce(
			(acc, certificate) => {
				acc[certificate.id] = true
				return acc
			},
			{} as Record<string, boolean>
		)
		hasErrors.value = props.selectedCertificates.some(certificate => certificate.error)
		hasWarnings.value = props.selectedCertificates.some(certificate => certificate.warning?.message && !isExcludedWarningMessage(certificate.warning.message))
		if (!hasErrors.value && !hasWarnings.value) {
			await certificateService.createPackage(certificatesMap, props.institutionId, packageName.value, props.isTestMode)
		} else {
			store.dispatch('notification/showError', 'No se puede crear un paquete con certificados que contengan errores o advertencias')
			return
		}

		store.dispatch('notification/showSuccess', 'Paquete creado exitosamente')
		showModal.value = false
		packageName.value = ''
	} catch (error) {
		handleError(error)
	}
}

/**
 * @description Función que maneja los errores
 * @param {any} error - Error a manejar
 */
const handleError = (error: any) => {
	if (error.message.includes('Unauthorized access to institution')) {
		store.dispatch('notification/showError', 'No tienes acceso a esta institución')
	} else {
		store.dispatch('notification/showError', error.message)
	}
}
const getResponsables = (certificate: ICertificate) => {
	const responsables: string[] = []
	for (const responsable of Object.keys(certificate.data?.FirmaResponsables)) {
		const fullName =
			certificate.data?.FirmaResponsables[responsable].nombre +
			' ' +
			certificate.data?.FirmaResponsables[responsable].primerApellido +
			' ' +
			certificate.data?.FirmaResponsables[responsable].segundoApellido
		responsables.push(fullName)
	}
	if (responsables.join(', ').includes('undefined')) {
		return Object.keys(certificate.data?.FirmaResponsables).join(', ')
	}
	return responsables.join(', ')
}
</script>

<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss background-color="transparent">
		<VaCard>
			<VaCardTitle :class="props.isTestMode ? 'bg-danger' : 'bg-primary'">
				<div class="row align-items-center" style="color: white">
					<div class="col-md-11 text-center">
						<div class="row">
							<div class="col-md-12 text-center" style="font-size: 22px; font-weight: bold">Crear nuevo paquete</div>
						</div>
					</div>
				</div>
			</VaCardTitle>
			<VaCardContent class="p-4">
				<div class="certificates-scroll">
					<VaInput v-model="packageName" label="Nombre del Paquete" class="mb-2" style="width: 100%" />
					<p class="font-medium mb-2">Elementos seleccionados ({{ selectedCertificatesCount }}):</p>
					<VaCard v-for="certificate in selectedCertificates" :key="certificate.id" class="mb-3" :color="'primary'" gradient hoverable>
						<VaCardContent>
							<div class="flex items-start justify-between">
								<div class="flex-1">
									<div class="font-semibold text-lg text-white mb-1">
										<VaIcon name="school" class="text-white/80" style="margin-right: 10px" />
										<span class="text-lg text-white/90 font-bold">
											{{ certificate.data?.Alumno?.nombre }}
											{{ certificate.data?.Alumno?.primerApellido }}
											{{ certificate.data?.Alumno?.segundoApellido }}
										</span>
										<div class="w-full text-right">
											<span class="text-sm text-white/90 font-bold">CURP: {{ certificate.data.Alumno.curp }}</span>
										</div>
									</div>
									<div class="flex items-center gap-2 mb-3">
										{{ certificate.data?.Carrera?.cveCarrera }}
										<span class="text-sm text-white/90">{{ certificate.data?.Carrera?.nombreCarrera }}</span>
									</div>
									<div class="flex items-center mb-3">
										<span class="text-sm text-white/90 ml-2">Responsables a firmar: {{ getResponsables(certificate) }}</span>
									</div>
									<div class="flex items-center gap-2">
										<div class="flex items-center gap-2">
											<i class="fas fa-book-open text-gray-400" style="margin-right: 5px; font-size: 11px"></i>
											<span style="font-weight: bold; font-size: 11px">Asignadas: {{ certificate.data.Asignaturas.asignadas }}</span>
										</div>
										<div class="flex items-center gap-2">
											<i class="fas fa-coins text-gray-400" style="margin-right: 5px; font-size: 11px"></i>
											<span style="font-weight: bold; font-size: 11px">Creditos Obtenidos: {{ certificate.data.Asignaturas.creditosObtenidos }}</span>
										</div>
										<div class="flex items-center gap-2">
											<i class="fas fa-coins text-gray-400" style="margin-right: 5px; font-size: 11px"></i>
											<span style="font-weight: bold; font-size: 11px">Total de créditos: {{ certificate.data.Asignaturas.totalCreditos }}</span>
										</div>
										<div class="flex items-center gap-2">
											<i class="fas fa-clock text-gray-400" style="margin-right: 5px; font-size: 11px"></i>
											<span style="font-weight: bold; font-size: 11px">Ciclos: {{ certificate.data.Asignaturas.numeroCiclos }}</span>
										</div>
										<div class="flex items-center gap-2">
											<i class="fas fa-award text-gray-400" style="margin-right: 5px; font-size: 11px"></i>
											<span style="font-weight: bold; font-size: 11px">Promedio: {{ certificate.data.Asignaturas.promedio }}</span>
										</div>
										<div class="flex items-center gap-2">
											<i class="fas fa-eye text-gray-400" style="margin-right: 5px; font-size: 11px"></i>
											<span style="font-weight: bold; font-size: 11px">Total : {{ certificate.data.Asignaturas.total }}</span>
										</div>
									</div>
								</div>
							</div>
						</VaCardContent>
					</VaCard>
				</div>
			</VaCardContent>
			<VaCardActions class="d-flex justify-content-end">
				<VaButton color="danger" @click="showModal = false" class="btn-cancel" style="padding: 8px 16px; font-weight: 500; letter-spacing: 0.5px" size="small">
					<VaIcon name="close" style="margin-right: 4px" />
					Cancelar
				</VaButton>
				<VaButton color="primary" @click="createPackage" class="btn-create" style="margin-left: 12px; padding: 8px 20px; font-weight: 600; letter-spacing: 0.5px" size="small">
					<VaIcon name="done" style="margin-right: 4px" />
					Crear Paquete
				</VaButton>
			</VaCardActions>
		</VaCard>
	</VaModal>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
.certificates-scroll {
	max-height: 70vh;
	overflow-y: auto;
	padding-right: 8px;
}
</style>
